import React, { useState, useEffect, useMemo } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { companyAPI } from '../../services/api'
import { toast } from '../../hooks/use-toast'
import {
  Calendar,
  Building2,
  Clock,
  Users,
  HelpCircle,
  Star,
  Trash2,
  AlertTriangle,
  Bell,
  Megaphone,
  Info,
  Trophy
} from 'lucide-react'

export default function Dashboard() {
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const isIntakeCompleted = user?.companyId && user?.intakeCompleted
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [announcement, setAnnouncement] = useState(null)
  const [loadingAnnouncement, setLoadingAnnouncement] = useState(true)
  const [networkingPassesLocked, setNetworkingPassesLocked] = useState(true)
  const [companyCategories, setCompanyCategories] = useState([])

  useEffect(() => {
    fetchAnnouncement()
    checkNetworkPassesLock()
    fetchCompanyInfo()
  }, [])

  const fetchCompanyInfo = async () => {
    if (user?.companyId) {
      try {
        const response = await companyAPI.getCompanyInfo()
        if (response.data.success && response.data.company) {
          setCompanyCategories(response.data.company.category || [])
        }
      } catch (error) {
        console.error('Error fetching company info:', error)
      }
    } else if (user?.companyCategory) {
      // Fallback to user object if available
      setCompanyCategories(user.companyCategory || [])
    }
  }

  const checkNetworkPassesLock = async () => {
    if (user?.companyId) {
      try {
        const response = await companyAPI.getNetworkingPasses()
        console.log('Network passes response (Dashboard):', response.data)
        if (response.data) {
          const isLocked = response.data.isLocked === true || response.data.networkingPassesLocked === true
          console.log('Setting networkingPassesLocked to (Dashboard):', isLocked)
          setNetworkingPassesLocked(isLocked)
        }
      } catch (error) {
        console.error('Error checking network passes lock:', error)
        setNetworkingPassesLocked(true)
      }
    }
  }

  const fetchAnnouncement = async () => {
    try {
      const response = await companyAPI.getAnnouncements()
      if (response.data.success) {
        setAnnouncement(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching announcement:', error)
    } finally {
      setLoadingAnnouncement(false)
    }
  }

  const handleDeleteTestData = async () => {
    try {
      setIsDeleting(true)
      const response = await companyAPI.deleteTestData()
      if (response.data.success) {
        toast.success('All test data deleted successfully. You will now be logged out.')
        logout()
        navigate('/login')
      }
    } catch (error) {
      console.error('Error deleting test data:', error)
      toast.error('Failed to delete test data. Please try again.')
    } finally {
      setIsDeleting(false)
      setShowDeleteDialog(false)
    }
  }

  return (
    <>
      <div className="grid gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* Welcome Card */}
        <Card className="col-span-full">
          <CardHeader>
            <CardTitle className="text-lg sm:text-xl">Welcome to IFTA 2025</CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Your preparation progress and key information
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className={`grid gap-3 sm:gap-4 sm:grid-cols-2 ${companyCategories.length > 0 ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
              <div className="flex items-center gap-2 sm:gap-3">
                <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
                <div>
                  <p className="text-xs sm:text-sm font-medium">Event Date</p>
                  <p className="text-xs sm:text-sm text-muted-foreground">December 10, 2025</p>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <Building2 className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
                <div>
                  <p className="text-xs sm:text-sm font-medium">Venue</p>
                  <p className="text-xs sm:text-sm text-muted-foreground">Aurika, Mumbai</p>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
                <div>
                  <p className="text-xs sm:text-sm font-medium">Time</p>
                  <p className="text-xs sm:text-sm text-muted-foreground">9:00 AM - 7:00 PM</p>
                </div>
              </div>
              {companyCategories.length > 0 && (
                <div className="flex items-start gap-2 sm:gap-3">
                  <Trophy className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm font-medium">
                      Nomination {companyCategories.length === 1 ? 'Category' : 'Categories'}
                    </p>
                    <ul className="text-xs sm:text-sm text-muted-foreground list-disc list-inside space-y-0.5">
                      {companyCategories.map((category, index) => (
                        <li key={index} className="truncate" title={category}>
                          {category}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t">
              <Button
                size="sm"
                className="h-7 sm:h-8 text-xs px-2 sm:px-3"
                onClick={() => {
                  // Create calendar event
                  const event = {
                    title: 'IFTA 2025 - India FinTech Awards (IFTA)',
                    details: 'We look forward to welcoming you to IFTA 2025.\n\nEvent details:\nDate: 10th December 2025\nTime: 9am to 7pm IST\n\nVenue: Aurika, Mumbai (near Mumbai International Airport)\nAddress: CTS No. 145 A, NS C-04, Skycity, Chhatrapati Shivaji Maharaj International Airport Rd, near Mumbai International Airport, AAI Colony, J B Nagar, Andheri East, Mumbai, Maharashtra 400099\n\nMap Link: https://maps.app.goo.gl/GvetoCK2WEa9e69w6\n\nAgenda: https://www.indiafintech.com/agenda\n\nWarm regards,\nTeam IFTA',
                    location: 'Aurika Mumbai, Andheri East',
                    startDate: '2025-12-10',
                    startTime: '09:00',
                    endDate: '2025-12-10',
                    endTime: '19:00'
                  }

                  // Google Calendar URL
                  const googleCalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&details=${encodeURIComponent(event.details)}&location=${encodeURIComponent(event.location)}&dates=${event.startDate.replace(/-/g, '')}T${event.startTime.replace(':', '')}00/${event.endDate.replace(/-/g, '')}T${event.endTime.replace(':', '')}00`

                  window.open(googleCalUrl, '_blank')
                }}
              >
                <Calendar className="h-3 w-3 mr-2" />
                Add to Calendar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions and Announcements Container */}
        <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2 col-span-full">
          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base sm:text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-2 sm:grid sm:grid-cols-2">
                <Button
                  variant="outline"
                  className="justify-start text-left h-auto min-h-[44px] py-3 px-4 gap-3"
                  onClick={() => navigate('/company/timeline')}
                >
                  <Building2 className="h-5 w-5 flex-shrink-0" />
                  <span className="text-sm font-medium">Company Details & Logo</span>
                </Button>
                <Button
                  variant="outline"
                  className="justify-start text-left h-auto min-h-[44px] py-3 px-4 gap-3"
                  onClick={() => {
                    if (networkingPassesLocked) {
                      toast.warning('Network pass allocation will be available soon. Please check back later.')
                    } else if (!isIntakeCompleted) {
                      toast.warning('Please complete the Company Details & Logo first to unlock this feature.')
                    } else {
                      navigate('/company/timeline')
                    }
                  }}
                  disabled={!isIntakeCompleted || networkingPassesLocked}
                >
                  <Users className="h-5 w-5 flex-shrink-0" />
                  <span className="text-sm font-medium">{networkingPassesLocked ? 'Passes (Coming Soon)' : 'Manage Passes'}</span>
                </Button>
                <Button
                  variant="outline"
                  className="justify-start text-left h-auto min-h-[44px] py-3 px-4 gap-3"
                  onClick={() => navigate('/company/timeline')}
                  disabled={!isIntakeCompleted}
                >
                  <Star className="h-5 w-5 flex-shrink-0" />
                  <span className="text-sm font-medium">Brand Promotion</span>
                </Button>
                <Button
                  variant="outline"
                  className="justify-start text-left h-auto min-h-[44px] py-3 px-4 gap-3"
                  onClick={() => navigate('/company/help')}
                >
                  <HelpCircle className="h-5 w-5 flex-shrink-0" />
                  <span className="text-sm font-medium">Get Support</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* TODO : Announcements */}
          {/* <Card>
            <CardHeader>
            <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Bell className="h-4 w-4" />
                  Announcements
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {!loadingAnnouncement && (
                <>
                  {announcement ? (
                    // Show admin announcement
                    <div className={`p-4 rounded-lg border ${
                      announcement.type === 'urgent' ? 'bg-red-50 border-red-200' :
                      announcement.type === 'warning' ? 'bg-amber-50 border-amber-200' :
                      announcement.type === 'success' ? 'bg-green-50 border-green-200' :
                      'bg-blue-50 border-blue-200'
                    }`}>
                      <div className="flex items-start gap-2">
                        <Info className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                          announcement.type === 'urgent' ? 'text-red-600' :
                          announcement.type === 'warning' ? 'text-amber-600' :
                          announcement.type === 'success' ? 'text-green-600' :
                          'text-blue-600'
                        }`} />
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-1">
                            {announcement.title}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {announcement.content}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Default DigiMeet promotion
                    <div className="p-4 rounded-lg bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200">
                      <div className="flex items-start gap-3">
                        <Megaphone className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-purple-900 mb-1">
                            Join DigiMeet 2025!
                          </h4>
                          <p className="text-sm text-purple-700 mb-2">
                            Connect with industry leaders and investors at India's premier fintech networking event.
                          </p>
                          <a
                            href="https://digimeet.indiafintech.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-medium text-purple-600 hover:text-purple-800 underline"
                          >
                            Learn more about DigiMeet →
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card> */}
        </div>

        {/* TODO : Testing Section - Delete Button */}
        {/* <Card className="lg:col-span-3 border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-lg text-red-600">Testing Tools</CardTitle>
            <CardDescription className="text-red-600">
              For internal testing only - DO NOT use in production
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="destructive"
              onClick={() => setShowDeleteDialog(true)}
              className="w-full sm:w-auto"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete All Test Data
            </Button>
            <p className="text-xs text-red-600 mt-2">
              This will delete all company data except the whitelist entry.
            </p>
          </CardContent>
        </Card> */}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Confirm Deletion
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 pt-3">
                <p className="font-semibold text-foreground">
                  Are you absolutely sure you want to delete all test data?
                </p>
                <p>This action will permanently delete:</p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Company profile and details</li>
                  <li>All tasks and progress</li>
                  <li>Networking passes</li>
                  <li>Presentations</li>
                  <li>User account</li>
                </ul>
                <p className="font-semibold text-red-600">
                  This action cannot be undone!
                </p>
                <p className="text-sm">
                  Note: Your company registration will remain for re-testing.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteTestData}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Everything
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}